import * as React from 'react'
import { cn } from '@/lib/utils'
import { Icon, IconName } from '@/components/ui/icon'

export interface InputProps extends React.ComponentProps<'input'> {
  icon?: IconName
  iconPosition?: 'left' | 'right'
  error?: boolean
  success?: boolean
  fullWidth?: boolean
  /** Visual density; not the native HTML `size` attribute. */
  fieldSize?: 'default' | 'sm'
  /** When false, removes visible border (Filter Bar fields). Default true. */
  bordered?: boolean
  /** Number steppers: `end` keeps native arrows at the trailing edge; `inset` adds extra right padding. */
  spinnerAlign?: 'inset' | 'end'
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, icon, iconPosition = 'left', error, success, fullWidth = true, fieldSize = 'default', bordered = true, spinnerAlign = 'end', onWheel, ...props }, ref) => {
    const hasIcon = !!icon
    const isSm = fieldSize === 'sm'
    const isNumber = type === 'number'
    const spinnerAtEnd = isNumber && spinnerAlign === 'end'
    const iconSize = isSm ? 14 : 16
    const iconInset = isSm ? 'left-2.5' : 'left-3'
    const iconInsetRight = isSm ? 'right-2.5' : 'right-3'
    const startPad = isSm ? 'pl-[var(--form-control-px-sm)]' : 'pl-[var(--form-control-px)]'
    const endPad = isNumber
      ? spinnerAtEnd
        ? isSm
          ? 'pr-[var(--form-control-px-sm)]'
          : 'pr-[var(--form-control-px)]'
        : isSm
          ? 'pr-8'
          : 'pr-10'
      : isSm
        ? 'pr-[var(--form-control-px-sm)]'
        : 'pr-[var(--form-control-px)]'
    const paddingClass = iconPosition === 'left'
      ? (hasIcon ? (isSm ? `pl-8 ${endPad}` : `pl-10 ${endPad}`) : `${startPad} ${endPad}`)
      : (hasIcon ? (isSm ? `pr-8 ${startPad}` : `pr-10 ${startPad}`) : `${startPad} ${endPad}`)
    const spinnerHeightClass = isSm
      ? '[&::-webkit-inner-spin-button]:h-3.5 [&::-webkit-inner-spin-button]:max-h-3.5 [&::-webkit-outer-spin-button]:h-3.5 [&::-webkit-outer-spin-button]:max-h-3.5'
      : '[&::-webkit-inner-spin-button]:h-4 [&::-webkit-inner-spin-button]:max-h-4 [&::-webkit-outer-spin-button]:h-4 [&::-webkit-outer-spin-button]:max-h-4'
    const numberSpinnerClass = isNumber
      ? cn(
          '[&::-webkit-inner-spin-button]:opacity-100 [&::-webkit-outer-spin-button]:opacity-100',
          '[&::-webkit-inner-spin-button]:cursor-pointer [&::-webkit-outer-spin-button]:cursor-pointer',
          spinnerHeightClass,
          '[&::-webkit-inner-spin-button]:my-auto [&::-webkit-outer-spin-button]:my-auto',
          spinnerAtEnd
            ? '[&::-webkit-inner-spin-button]:mx-0 [&::-webkit-outer-spin-button]:mx-0'
            : '[&::-webkit-inner-spin-button]:ml-0 [&::-webkit-inner-spin-button]:mr-1.5 [&::-webkit-outer-spin-button]:ml-0 [&::-webkit-outer-spin-button]:mr-1.5'
        )
      : undefined
    const widthClass = fullWidth ? 'w-full min-w-0 max-w-full' : 'w-auto max-w-full'

    const inputRef = React.useRef<HTMLInputElement | null>(null)
    const refRef = React.useRef(ref)
    refRef.current = ref
    // Ref callback must never call setState — it can run multiple times in one commit and causes "Maximum update depth exceeded".
    const setRef = React.useCallback((el: HTMLInputElement | null) => {
      inputRef.current = el
      const r = refRef.current
      if (typeof r === 'function') r(el)
      else if (r) (r as React.MutableRefObject<HTMLInputElement | null>).current = el
    }, [])
    // Wheel handler: use inputRef.current so we don't need state (and avoid setState in ref callback).
    React.useLayoutEffect(() => {
      const el = inputRef.current
      if (type !== 'number' || !el) return
      const handler = (e: WheelEvent) => {
        e.preventDefault()
        e.stopPropagation()
        onWheel?.(e as unknown as React.WheelEvent<HTMLInputElement>)
      }
      el.addEventListener('wheel', handler, { passive: false })
      return () => el.removeEventListener('wheel', handler)
    }, [type, onWheel])
    const handleWheel = type === 'number' ? undefined : onWheel

    return (
      <div className={cn('relative min-w-0 max-w-full', widthClass)}>
        {icon && iconPosition === 'left' && (
          <Icon
            name={icon}
            size={iconSize}
            className={cn(
              'absolute top-1/2 -translate-y-1/2 text-muted-foreground',
              iconInset,
              error && 'text-destructive',
              success && 'text-secondary' // Use secondary (green) for success in Vidhyantra
            )}
          />
        )}
        <input
          type={type}
          className={cn(
            'box-border block w-full min-w-0 max-w-full rounded-[var(--form-control-radius)] bg-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[hsl(var(--border-interactive))] focus-visible:border-[hsl(var(--border-interactive))] dark:focus-visible:ring-ring dark:focus-visible:border-primary focus-visible:border-opacity-50 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 transition-colors',
            bordered && 'border border-input',
            !bordered && 'border-0 shadow-none',
            isSm
              ? cn(
                  'h-[var(--form-control-height-sm)] text-sm leading-8',
                  isNumber ? 'py-0' : 'py-1'
                )
              : cn(
                  'h-[var(--form-control-height)] text-base md:text-sm',
                  isNumber ? 'py-0' : 'py-[var(--form-control-py)]'
                ),
            fullWidth ? 'w-full min-w-0 max-w-full' : 'w-auto max-w-full',
            paddingClass,
            numberSpinnerClass,
            !error && !success && 'border-input border-opacity-100',
            className,
            error &&
              'border-destructive focus-visible:border-destructive focus-visible:ring-destructive focus-visible:ring-2 focus-visible:ring-inset',
            success && 'border-secondary focus-visible:border-secondary focus-visible:border-opacity-70'
          )}
          ref={setRef}
          {...props}
          onWheel={handleWheel}
        />
        {icon && iconPosition === 'right' && (
          <Icon
            name={icon}
            size={iconSize}
            className={cn(
              'absolute top-1/2 -translate-y-1/2 text-muted-foreground',
              iconInsetRight,
              error && 'text-destructive',
              success && 'text-secondary'
            )}
          />
        )}
      </div>
    )
  }
)
Input.displayName = 'Input'

export { Input }
